/* eslint-disable max-lines */
import { ILand, IOffer, TOfferCategory, TOfferType } from '@cian/chats-api/lib/browser-entry';

import { numberToPrettyString } from './number';

export function objIsOffer(obj: {}): obj is IOffer {
  return (
    typeof obj === 'object' &&
    'bargainTerms' in obj &&
    'category' in obj &&
    'dealType' in obj &&
    'geo' in obj &&
    'offerType' in obj
  );
}

export function createOfferPresenter(offer: IOffer) {
  return {
    commercial: {
      getCommercialLandPerMeterByYear: () => getCommercialLandPerMeterByYear(offer),
      getCommercialLandUnit: () => getCommercialLandUnit(offer),
      getCommercialPerMeterByYear: () => getCommercialPerMeterByYear(offer),
      getCommercialRentPrice: () => getCommercialRentPrice(offer),
      isCommercialLand: () => isCommercialLand(offer),
    },
    getLandArea: () => getLandArea(offer),
    getLandUnitType: () => getLandUnitType(offer),
    getRentPrice: () => getRentPrice(offer),
    getRoomArea: () => getRoomArea(offer),
    getSalePrice: () => getSalePrice(offer),
    getSalePricePerMeter: () => getSalePricePerMeter(offer),
    getTotalArea: () => getTotalArea(offer),
    getType: () => getTypeDict(offer),
    helpers: {
      getArea: () => getArea(offer),
      getPrice: () => getPrice(offer),
    },
    isCommercial: () => isCommercial(offer.offerType),
    isFlat: () => isFlat(offer),
    isLand: () => isLand(offer),
    isSuburban: () => isSuburban(offer),
  };
}

function isFlat(offer: IOffer): boolean {
  return offer.offerType === 'flat';
}

function isCommercial(offerType: TOfferType) {
  return offerType === 'commercial';
}

function isSuburban(offer: IOffer): boolean {
  return offer.category
    ? Array<TOfferCategory>(
        'cottageRent',
        'cottageSale',
        'dailyHouseRent',
        'houseRent',
        'houseSale',
        'houseShareRent',
        'houseShareSale',
        'landRent' as TOfferCategory,
        'landSale',
        'townhouseRent',
        'townhouseSale',
      ).includes(offer.category)
    : false;
}

function isLand(offer: IOffer) {
  return offer.category
    ? Array<TOfferCategory>('landRent' as TOfferCategory, 'landSale').includes(offer.category)
    : false;
}

function isCommercialLand(offer: IOffer) {
  return (
    offer.category &&
    (['commercialLandRent', 'commercialLandSale'] as TOfferCategory[]).includes(offer.category) &&
    'land' in offer
  );
}

function getCommercialLandUnit(offer: IOffer) {
  if (!isCommercialLand(offer)) {
    return null;
  }

  return {
    text: (offer.land as ILand).areaUnitType === 'sotka' ? 'сот.' : 'га.',
    type: (offer.land as ILand).areaUnitType,
  };
}

// Название
function getTypeDict(offer: IOffer) {
  const { category, roomsCount, flatType } = offer;

  // tslint:disable-next-line:switch-default
  switch (category) {
    case 'buildingSale':
    case 'buildingRent':
      return 'Здание';
    case 'businessRent':
    case 'businessSale':
      return 'Готовый бизнес';
    case 'carServiceRent':
    case 'carServiceSale':
      return 'Автосервис';
    case 'domesticServicesRent':
    case 'domesticServicesSale':
      return 'Быт. Услуги';
    case 'publicCateringRent':
    case 'publicCateringSale':
      return 'Общепит';
    case 'bedRent':
    case 'dailyBedRent':
      return 'Койко-место';
    case 'commercialLandRent':
    case 'commercialLandSale':
      return 'Комм. земля';
    case 'cottageRent':
    case 'cottageSale':
      return 'Коттедж';
    case 'houseShareRent':
    case 'houseShareSale':
      return 'часть дома';
    case 'dailyRoomRent':
      return 'Комната';
    case 'dailyHouseRent':
    case 'houseRent':
    case 'houseSale':
      return 'Дом';
    case 'warehouseRent':
    case 'warehouseSale':
      return 'Склад';
    case 'townhouseRent':
    case 'townhouseSale':
      return 'Таунхаус';
    case 'roomRent':
    case 'roomSale':
      return 'Комната';
    case 'shoppingAreaRent':
    case 'shoppingAreaSale':
      return 'Торг. площадь';
    case 'officeRent':
    case 'officeSale':
      return 'Офис';
    case 'flatRent':
    case 'flatSale':
    case 'dailyFlatRent':
    case 'newBuildingFlatSale':
      if (roomsCount) {
        if (roomsCount <= 5) {
          return `${roomsCount}-комн.`;
        }

        return 'Многокомн.';
      }

      if (flatType) {
        if (flatType === 'studio') {
          return 'Студия';
        }

        if (flatType === 'openPlan') {
          return 'Своб. план';
        }
      }

      return 'Квартира';
    case 'garageRent':
    case 'garageSale':
      return 'Гараж';
    case 'flatShareSale':
      return 'Доля';
    // Этого типа нет в сваггере ФП, но есть в серпе
    case 'landRent' as TOfferCategory:
    case 'landSale':
      return 'Участок';
    case 'freeAppointmentObjectRent':
    case 'freeAppointmentObjectSale':
      return 'ПСН';
    case 'industryRent':
    case 'industrySale':
      return 'Производство';
  }

  return '';
}

// Price
function getPaymentPeriod(offer: IOffer): string {
  return offer.category && offer.category.indexOf('daily') !== -1 ? 'сутки' : 'мес.';
}

function getRentPrice(offer: IOffer): string {
  let price = offer.bargainTerms.priceRur || offer.bargainTerms.priceRur;

  if (offer.bargainTerms.utilitiesTerms && !offer.bargainTerms.utilitiesTerms.includedInPrice) {
    price += offer.bargainTerms.utilitiesTermsPrices.rur;
  }

  return `${numberToPrettyString(price)} \u20bd/${getPaymentPeriod(offer)}`;
}

function getCommercialRentPrice(offer: IOffer): string {
  const price = offer.priceTotalPerMonthRur;

  return `${numberToPrettyString(price)} \u20bd/${getPaymentPeriod(offer)}`;
}

function getCommercialPerMeterByYear(offer: IOffer): string {
  return `${numberToPrettyString(offer.pricePerUnitAreaPerYearRur)} \u20bd за м\u00b2 в год`;
}

function getCommercialLandPerMeterByYear(offer: IOffer) {
  const unit = getCommercialLandUnit(offer);

  return `${numberToPrettyString(offer.pricePerUnitAreaPerYearRur)} \u20bd за ${unit ? unit.text : 'м\u00b2'} в год`;
}

function getSalePrice(offer: IOffer): string | null {
  return `${numberToPrettyString(offer.bargainTerms.priceRur)} \u20bd`;
}

function getSalePricePerMeter(offer: IOffer): React.ReactNode | null {
  if (!offer.bargainTerms) {
    return null;
  }

  if (offer.bargainTerms.priceType !== 'all') {
    return `${numberToPrettyString(offer.bargainTerms.priceRur)} \u20bd`;
  } else if (offer.totalArea) {
    const totalArea = parseFloat(offer.totalArea);

    if (totalArea > 0) {
      const pricePerMeter = Math.round(offer.bargainTerms.priceRur / totalArea);
      const pricePerMeterFormatted = numberToPrettyString(pricePerMeter);

      return `${pricePerMeterFormatted} \u20bd за м\u00b2`;
    }
  }

  return null;
}

// Area
function getLandArea(offer: IOffer) {
  return offer.land && offer.land.area ? String(offer.land.area) : null;
}

function getLandUnitType(offer: IOffer) {
  return offer.land && offer.land.areaUnitType ? offer.land.areaUnitType : null;
}

function getRoomArea(offer: IOffer): string | null {
  if (offer.roomArea) {
    return `${Math.round(parseFloat(offer.roomArea))}`;
  }

  return null;
}

function getTotalArea(offer: IOffer): string | null {
  if (offer.totalArea) {
    return String(Math.round(parseFloat(offer.totalArea)));
  }

  return null;
}

// Helpers
function getPrice(offer: IOffer) {
  if (isCommercial(offer.offerType)) {
    if (offer.dealType === 'rent') {
      return getCommercialRentPrice(offer);
    } else {
      return getSalePrice(offer);
    }
  } else {
    if (offer.dealType === 'rent') {
      return getRentPrice(offer);
    } else {
      return getSalePrice(offer);
    }
  }
}

function getArea(offer: IOffer) {
  /**
   * Площадь для коммерческой земли
   */
  if (isLand(offer) || isCommercialLand(offer)) {
    const landArea = getLandArea(offer);

    if (landArea) {
      return `${parseFloat(landArea)} ${getLandUnitType(offer) === 'sotka' ? 'сот.' : 'га.'}`;
    }
  }

  /**
   * Площадь для комнаты
   */
  if (offer.flatType && offer.flatType === 'rooms') {
    const roomArea = getRoomArea(offer);

    if (roomArea) {
      return `${roomArea} м\u00B2`;
    }
  }

  /**
   * Площадь для всей квартиры/бизнес цента/офиса/торговой площади
   */
  const totalArea = getTotalArea(offer);

  if (totalArea) {
    return `${totalArea} м\u00B2`;
  }

  return '';
}
